<template>
  <v-container class="mb-4">
    <v-row dense>
      <v-col>
        <span
          class="title"
          v-html="
            $t('exhibitionArea.welcomeText', {
              company: currentExhibitor.vendorName,
            })
          "
        />
      </v-col>
      <v-col v-if="booths.length > 1" cols="12">
        <v-select
          v-model="currentExhibitor"
          :items="booths"
          item-text="title"
          item-value="_id"
          :label="$t('exhibitionArea.booth')"
          outlined
          dense
          @change="updateCurrentExhibitorSelection"
        >
        </v-select>
      </v-col>
      <v-col cols="12">
        <div
          class="text-h6"
          v-html="
            $t('exhibitionArea.boothHeadline', {
              booth: currentExhibitor.title,
            })
          "
          v-if="booths.length <= 1"
        />
        <div
          class="text-body-1"
          v-if="pageType != 'ICSMGE'"
          v-html="
            $t('exhibitionArea.exhibitionInformationText', {
              category: currentExhibitor.category,
              size: currentExhibitor.size,
              desks: currentExhibitor.desks,
              chairs: currentExhibitor.chairs,
              amountPersons: currentExhibitor.amountPersons,
              amountAdditionalPersons: currentExhibitor.amountAdditionalPersons,
            })
          "
        />
        <div class="text-body-1" v-else>
          Category: {{ currentExhibitor.category }} ({{
            currentExhibitor.size
          }}
          m²)<br />
          <template v-if="currentExhibitor.bookedPackage == 'FULL_PACKAGE'">
            Desks: {{ currentExhibitor.desks }}<br />
            Chairs: {{ currentExhibitor.chairs }}<br />
          </template>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="persons.length">
      <v-col cols="12" class="text-h6">
        {{ $t("exhibitionArea.boothStaff") }}
      </v-col>
    </v-row>
    <v-row
      v-for="(person, index) in persons"
      :key="`person-${index}`"
      dense
      class="mb-n6"
    >
      <v-col
        cols="12"
        v-if="index === currentExhibitor.amountPersons"
        class="mt-n2 mb-n2"
      >
        <v-divider color="red" />
        <v-subheader class="mt-n7 red--text font-weight-bold">
          {{ $t("exhibitionArea.chargeable") }}
        </v-subheader>
      </v-col>
      <v-col cols="4">
        <v-text-field
          :label="$t('exhibitionArea.firstName')"
          v-model="person.firstName"
          outlined
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          :label="$t('exhibitionArea.lastName')"
          v-model="person.lastName"
          outlined
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          :label="$t('exhibitionArea.company')"
          v-model="person.company"
          outlined
        >
          <template
            #append-outer
            v-if="index >= currentExhibitor.amountPersons"
          >
            <v-chip
              :color="person.paid ? 'success' : 'primary'"
              dark
              class="mt-n1"
            >
              {{ $t(`exhibitionArea.${person.paid ? "paid" : "notPaid"}`) }}
            </v-chip>
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="removePerson(index)"
              v-if="!person.paid"
              class="mt-n2"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col
        cols="12"
        v-if="index === currentExhibitor.amountPersons - 1"
        class="mt-n4 mb-8"
      >
        <v-btn
          color="primary"
          :disabled="!hasPersonChanges"
          @click="savePersons"
        >
          {{ $t("exhibitionArea.save") }}
        </v-btn>
        <v-btn
          class="float-right"
          color="primary"
          @click="addPerson(false)"
          v-if="canBookPersons"
        >
          <v-icon>mdi-plus</v-icon>
          {{ $t("exhibitionArea.addPaidPerson") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="totalAmount > 0 || currentExhibitor.bills.length">
      <v-col cols="12" class="text-h6">
        {{ $t("exhibitionArea.openAmount") }}
      </v-col>
      <v-col cols="12">
        <v-simple-table dense>
          <template #default>
            <thead>
              <tr>
                <th v-html="$t('exhibitionArea.description')" />
                <th v-html="$t('exhibitionArea.price')" />
              </tr>
            </thead>
            <tbody v-if="pageSettings.additionalPricePerPerson">
              <tr
                v-for="(additionalPerson, index) in additionalPersons"
                :key="`additional-person-amount-${index}`"
              >
                <td
                  v-if="additionalPerson.paid"
                  v-html="getAdditionalBoothStaffText(index)"
                />
                <td v-else>
                  <v-checkbox
                    v-model="selectedPersons[index]"
                    dense
                    :label="getAdditionalBoothStaffText(index)"
                  />
                </td>
                <td
                  v-html="'€ ' + pageSettings.additionalPricePerPerson"
                  v-if="!additionalPerson.paid"
                />
                <td v-html="$t('exhibitionArea.paid')" v-else />
              </tr>
              <tr
                v-for="(invoice, index) in currentExhibitor.bills || []"
                :key="`invoice-${index}`"
              >
                <td>
                  <span
                    v-html="
                      $t('exhibitionArea.partialInvoice') + ' ' + (index + 1)
                    "
                    v-if="invoice.paid"
                  />
                  <v-checkbox
                    v-model="selectedBills[index]"
                    dense
                    :label="
                      $t('exhibitionArea.partialInvoice') + ' ' + (index + 1)
                    "
                    v-else
                  />
                  <v-btn
                    icon
                    outlined
                    small
                    color="primary"
                    v-if="invoice.invoiceUrl"
                    @click="downloadDocument(invoice.invoiceUrl)"
                  >
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                </td>
                <td v-html="'€ ' + invoice.amount" v-if="!invoice.paid" />
                <td v-html="$t('exhibitionArea.paid')" v-else />
              </tr>
              <tr class="table-sum">
                <td v-html="$t('exhibitionArea.finalPrice')" />
                <td v-html="'€ ' + totalAmount" />
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
      <v-col cols="12" class="mb-4" v-if="totalAmount > 0">
        <v-btn color="primary" @click="redirectToPayment">
          <v-icon small left>mdi-wallet</v-icon>
          {{ $t("exhibitionArea.pay", { amount: totalAmount }) }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  data: () => ({
    persons: [],
    selectedPersons: [],
    selectedBills: [],
    initialPersons: "",
  }),

  created() {
    this.updateDetails();
    const queryParams = new URLSearchParams(window.location.search);
    const paymentIntentClientSecret = queryParams.get(
      "payment_intent_client_secret"
    );
    if (paymentIntentClientSecret) {
      this.redirectToPayment();
    }
  },

  methods: {
    ...mapActions(["updateExhibitor", "updateCurrentExhibitor"]),
    updateDetails() {
      this.persons = [...(this.currentExhibitor.persons || [])];
      while (this.persons.length < this.currentExhibitor.amountPersons) {
        this.addPerson(true);
      }
      this.initialPersons = JSON.stringify(this.persons);
      this.selectedPersons = [];
      for (let i = 0; i < this.currentExhibitor.amountAdditionalPersons; ++i) {
        this.selectedPersons.push(true);
      }
      this.selectedBills = [];
      for (let i = 0; i < this.currentExhibitor.bills.length; ++i) {
        this.selectedBills.push(true);
      }
    },
    updateCurrentExhibitorSelection(currentExhibitorId) {
      this.updateCurrentExhibitor(currentExhibitorId);
      this.updateDetails();
    },
    addPerson(paid) {
      this.persons.push({
        firstName: "",
        lastName: "",
        company: "",
        paid,
      });
    },
    removePerson(index) {
      this.persons.splice(index, 1);
    },
    async savePersons() {
      await this.updateExhibitor(this.persons);
      this.initialPersons = JSON.stringify(this.persons);
    },
    downloadDocument(url) {
      window.open(url, "_blank");
    },
    async redirectToPayment() {
      await this.savePersons();
      this.$router.push({
        name: "booth-payment-page",
        params: {
          selectedBills: this.selectedBills,
          selectedPersons: this.selectedPersons,
        },
      });
    },
    getAdditionalBoothStaffText(index) {
      let toReturn = this.$t("exhibitionArea.additionalBoothStaff").toString();
      const additionalPerson = this.additionalPersons[index];
      const names = [];
      if (additionalPerson.firstName) {
        names.push(additionalPerson.firstName);
      }
      if (additionalPerson.lastName) {
        names.push(additionalPerson.lastName);
      }
      const fullname = names.join(" ");
      if (fullname.length) {
        toReturn += ": " + fullname;
      }
      return toReturn;
    },
  },

  computed: {
    ...mapGetters(["pageSettings", "booths", "currentExhibitor", "pageType"]),
    canBookPersons() {
      return (
        this.persons.length <
        this.currentExhibitor.amountPersons +
          this.currentExhibitor.amountAdditionalPersons
      );
    },
    hasPersonChanges() {
      return JSON.stringify(this.persons) !== this.initialPersons;
    },
    additionalPersons() {
      const additionalPersons = [];
      for (
        let i = this.currentExhibitor.amountPersons;
        i < this.persons.length;
        ++i
      ) {
        additionalPersons.push(this.persons[i]);
      }
      return additionalPersons;
    },
    totalAmount() {
      return (
        this.additionalPersons.filter(
          (entry, index) => !entry.paid && this.selectedPersons[index]
        ).length *
          (this.pageSettings.additionalPricePerPerson || 0) +
        (this.currentExhibitor.bills || [])
          .filter((entry, index) => !entry.paid && this.selectedBills[index])
          .map((entry) => +entry.amount || 0)
          .reduce((a, b) => a + b, 0)
      );
    },
  },
};
</script>
<style scoped>
.table-sum > td {
  border-top: 1px dashed black !important;
  border-bottom: 3px double black !important;
  font-weight: bold;
}
</style>
